import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useImmer } from 'use-immer';

import Grid from '@mui/material/Grid2';
import { 
  ModelBase,
} from "@models";
import { 
  Character,
  TrueCreatureInstance,
  CreatureAbility,
  MinionAbility,
  Spell,
  SummonOption2,
  CharacterAbility,
  CharacterSpell,
  CreatureGift,
  CharacterCondition,
  RollPlus,
  RolledValue,
  iCreatureSpell,
  CreatureInstance,
} from "@dndModels";
import { SIZE_MAP, XP_FOR_CR } from "@dndModels/Constants";
import { AbilityScoreType, SpellLevel } from '@dndModels/Types';

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';

import LoadingBox from "@components/input/LoadingBox";

import CharacterResourceBoxesNew from "@dndComponents/character/misc/CharacterResourceBoxes";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ConditionBar from "@dndComponents/other/ConditionBar";
import CreatureEffect from "@dndComponents/character/spells_and_abilities/effects/CreatureEffect";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import HPInput from "@dndComponents/other/HPInput";
import ToastBox from '@components/display/ToastBox';
import Roller from '@dndComponents/other/Roller';
import StringBox from '@components/input/strings/StringBox';
import NotesButton from '@components/input/buttons/NotesButton';
import BasicImageButton from '@components/input/buttons/icon_buttons/BasicImageButton';
import FileDropzone from '@components/input/FileDropzone';
import CreatureInstanceActionsInput from './CreatureInstanceActionsInput';
import CharacterSettings from '@dndComponents/character/customizations/CharacterSettings';

interface Props { 
  char?: Character;
  creature_instance: CreatureInstance;
  trim?: boolean;
  true_id: string;
  view_only?: boolean;
  onChange?: (changed: CreatureInstance) => void;
  // Called when the Settings (Customizations) modal closes, after the creature instance has been rebuilt and before onChange.
  onCustomizationsChanged?: (changed: CreatureInstance) => Promise<void>;
  onSummon?: (spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: SpellLevel, selected_option: SummonOption2) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CreatureInstanceInput({ 
  char,
  creature_instance,
  trim = false,
  true_id,
  view_only = false,
  onChange,
  onCustomizationsChanged,
  onSummon
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload_resources, setReloadResources] = useState(false);
  const [selected_spell, setSelectedSpell] = useState<Spell | null>(null);
  const [prev_true_id, setTrueId] = useState(true_id);
  const [ping, setPing] = useState(false);
  const [expanded, setExpanded] = useImmer<{ [section: string]: boolean }>({});
  const mounted = useRef(false);
  const settings_open = useRef(false);
  const rebuild_timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const build_chain = useRef<Promise<void>>(Promise.resolve());
  const [creature_gifts, setCreatureGifts] = useState<CreatureGift[]>([]);
  const [popoverAbility, setPopoverAbility] = useState("");
  const [popoverTime, setPopoverTime] = useState(-1);
  const [roll, setRoll] = useState<RolledValue[]>([]);
  const [changing_name, setChangingName] = useState(false);
  const [notes_open, setNotesOpen] = useState(false);
  const [image_open, setImageOpen] = useState(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    // Reloading while the Settings modal is open would unmount it, so wait until it closes.
    if (!settings_open.current) {
      setReloading(true);
    }
  }

  // Builds are chained so two of them never run on the same creature instance at once.
  const rebuild = (ci: CreatureInstance) => {
    build_chain.current = build_chain.current.then(async () => {
      try {
        await char_util.buildMind(ci);
      } catch (err) {
        console.error("CreatureInstanceInput rebuild failed", err);
      }
    });
    return build_chain.current;
  }

  // Minions feed Recalculate through to their Character, which rebuilds them.
  // Standalone creature instances (like in Encounters) need to rebuild themselves.
  const recalculate = (ci: CreatureInstance) => {
    if (!ci.subscription_holder.feedTo) {
      if (rebuild_timeout.current) {
        clearTimeout(rebuild_timeout.current);
      }
      rebuild_timeout.current = setTimeout(() => {
        rebuild_timeout.current = undefined;
        rebuild(ci);
      }, 1000);
    }
  }

  const settingsClosed = async () => {
    settings_open.current = false;
    if (rebuild_timeout.current) {
      clearTimeout(rebuild_timeout.current);
      rebuild_timeout.current = undefined;
    }
    if (!creature_instance.subscription_holder.feedTo) {
      await rebuild(creature_instance);
    }
    if (onCustomizationsChanged) {
      try {
        await onCustomizationsChanged(creature_instance);
      } catch (err) {
        console.error("CreatureInstanceInput onCustomizationsChanged failed", err);
      }
    }
    if (onChange) {
      onChange(creature_instance);
    } else if (char) {
      char.subscription_holder.recalculate();
      char.changed();
    }
    // if (mounted.current) {
    //   setTimeout(() => {
    //     setReloading(true);
    //   }, 100);
    // }
  }

  const resourcesChanged = () => {
    console.log("resourcesChanged()"); // I think it's not working because it's using a clone of the object somewhere
    // setReloading(true);
    setReloadResources(true);
  }

  const loadCreature = useCallback(async () => {
    if (creature_instance instanceof TrueCreatureInstance) {
      // debugger;
      setLoading(true);
      try {
        if (creature_instance.creature) {
          await api.loadObject(creature_instance.creature);
          // await creature_instance.connectCreature(creature_instance.creature);
        }
        const res = await char_util.getCreatureGiftsForCreature({ char, creature: creature_instance });
        setCreatureGifts(res);
      } catch (err) {
        console.error("CreatureInstanceInput loadCreature failed", err);
      }
      setLoading(false);
    }
  }, [creature_instance, char, setLoading, setCreatureGifts]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      loadCreature();
    }
  }, [fetched, setFetched, loadCreature]);

  // Subscribe to whichever creature instance is being shown (it changes when selecting another one in a group).
  useEffect(() => {
    const ci = creature_instance;
    ci.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    ci.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
    ci.subscription_holder.subscribe("Recalculate", mounted, () => { recalculate(ci); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creature_instance]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (reload_resources) {
      setReloadResources(false);
    }
  }, [reload_resources, setReloadResources]);

  useEffect(() => {
    if (true_id !== prev_true_id) {
      setTrueId(true_id);
      setReloading(true)
    }
  }, [true_id, prev_true_id, setTrueId, setReloading]);

  const disabled = api.user === null || !api.online || (char !== undefined && !(char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id)));
  
  const ci = creature_instance;

  // console.log(creature);
  const renderExtras = () => {
    return (
      <>
        <ToastBox
          openTime={ popoverTime }
        >
          { renderRoll() }
        </ToastBox>
      </>
    );
  }

  const renderRoll = () => {
    return (
      <Roller 
        name={popoverAbility}
        rolled_values={roll}
        type="Ability Check" 
      />
    );
  }

  const renderHP = () => {
    // debugger;
    const hd_string = ci.hit_dice_string;
    // return (
    //   <p><strong>HP</strong> {creature.hp.max} ({hd_string})</p>
    // );
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={6}>
          <HPInput
            char={ci}
            disabled={disabled}
          />
        </Grid>
        <Grid size={6}></Grid>
        { ci.hit_dice.some(o => o.size > 0 && o.count > 0) &&
          <Grid size={6} className="centered">({hd_string})</Grid>
        }
      </Grid>
    )
  }

  const renderStat = (stat: AbilityScoreType) => {
    return (
      <tr>
        <th>{stat}</th>
        <td>
          {ci.current_ability_scores.getAbilityScore(stat)}
        </td>
        <td className="clickable" 
          onClick={() => {
            const rolls: RollPlus[] = [];
            const roll_plus = new RollPlus();
            roll_plus.ability_score = stat;
            rolls.push(roll_plus);
            // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
            const advantage = 0;
            setRoll(char_util.rollD20({ char: ci, rolls, type: "Ability Check", advantage }));
            setPopoverAbility(stat);
            setPopoverTime(common_util.get_time_number());
          }}>
          {common_util.addPlusMaybe(ci.current_ability_scores.getModifier(stat))}
        </td>
        <td className="clickable" 
          onClick={() => {
            const rolls: RollPlus[] = [];
            const roll_plus = new RollPlus();
            roll_plus.flat = ci.saving_throw(stat);
            rolls.push(roll_plus);
            // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
            const advantage = 0;
            setRoll(char_util.rollD20({ char: ci, rolls, type: "Saving Throw", advantage }));
            setPopoverAbility(stat + " Saving Throw");
            setPopoverTime(common_util.get_time_number());
          }}>
          {common_util.addPlusMaybe(ci.saving_throw(stat))}
        </td>
      </tr>
    );
  }

  const renderStats = () => {
    return (
      <div className="stats">
        <table className="physical abilities-saves">
          <thead>
            <tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>
          </thead>
          <tbody>
            { renderStat("STR") }
            { renderStat("DEX") }
            { renderStat("CON") }
          </tbody>
        </table>
        <table className="mental abilities-saves">
          <thead>
            <tr><th>&nbsp;</th><th>&nbsp;</th><th>Mod</th><th>Save</th></tr>
          </thead>
          <tbody>
            { renderStat("INT") }
            { renderStat("WIS") }
            { renderStat("CHA") }
          </tbody>
        </table>
      </div>
    );
  }

  const renderSkill = ({ skill_id, prof_level, skill_name, modifier }: { skill_id: string, prof_level: number, skill_name: string, modifier: number }) => {
    return (
      <span key={skill_id}>
        { skill_name } {common_util.addPlusMaybe(modifier)}
      </span>
    );
  }

  const renderSkills = () => {
    if (ci.skill_proficiencies2.length > 0) {
      return (
        <p>
          <strong>Skills</strong>&nbsp;
          {ci.skill_proficiencies2.map((sp, key) => {
            return (
              <span key={sp.skill_id} 
                className="clickable" 
                onClick={() => {
                  const rolls: RollPlus[] = [];
                  const roll_plus = new RollPlus();
                  roll_plus.flat = sp.modifier;
                  rolls.push(roll_plus);
                  // const { advantage } = creature instanceof CreatureInstance || creature instanceof SummonInstance ? { advantage: 0 } : char_util.getAdvantage(creature, "Attack", stat);
                  const advantage = 0;
                  setRoll(char_util.rollD20({ char: ci, rolls, type: "Skill Check", advantage }));
                  setPopoverAbility(sp.skill_name);
                  setPopoverTime(common_util.get_time_number());
                }}>
                { key > 0 && ", " }
                { renderSkill(sp) }
              </span>
            );
          })}
        </p>
      );
    }
    return null;
  }

  const renderVulnerabilities = () => {
    if (ci.vulnerabilities_string.length > 0) {
      return (
        <p><strong>Vulnerabilities</strong> {ci.vulnerabilities_string}</p>
      );
    }
    return null;
  }

  const renderResistances = () => {
    if (ci.resistances_string.length > 0) {
      return (
        <p><strong>Resistances</strong> {ci.resistances_string}</p>
      );
    }
    return null;
  }

  const renderImmunities = () => {
    if (ci.immunities_string.length > 0) {
      if (ci.condition_immunities.length > 0) {
        return (
          <p><strong>Immunities</strong> {ci.immunities_string}; {ci.condition_immunities_string}</p>
        );
      } else {
        return (
          <p><strong>Immunities</strong> {ci.immunities_string}</p>
        );
      }
    } else if (ci.condition_immunities.length > 0) {
      return (
        <p><strong>Immunities</strong> {ci.condition_immunities_string}</p>
      );
    }
    return null;
  }

  const renderSenses = () => {
    // debugger;
    const senses_string = ci.senses_string.length > 0 ? ` ${ci.senses_string};` : "";
    return (
      <p><strong>Senses</strong>{senses_string} Passive Perception {ci.passive_perception}</p>
    );
  }

  const renderLanguages = () => {
    return (
      <p><strong>Languages</strong>&nbsp;
        { ci.mind.languages_known.length > 0 ?
          <>
            <DisplayObjects type="language" ids={ci.mind.languages_known} />, { ci.mind.extra_languages }
          </>
          :
          <>
            { ci.mind.extra_languages }
          </>
        }
      </p>
    );
  }

  const renderCreatureInstanceConditions = () => {
    const active_conditions = creature_instance.condition_hash;
    return (
      <ConditionBar
        char_int={char}
        active_conditions={active_conditions}
        starred={[]}
        immune={creature_instance.condition_immunities}
        // extra_ids={["Any"]}
        disabled={disabled}
        onChange={(condition_id: string, level: number) => {
          const new_obj = creature_instance;
          if (level === 0) {
            // delete new_obj.condition_hash[condition_id];
            new_obj.conditions = new_obj.conditions.filter(o => o.source_id !== condition_id);
          } else {
            let char_cond = new_obj.conditions.find(o => o.source_id === condition_id);
            if (!char_cond) {
              const cond = data_util.conditions.find(o => o._id === condition_id);
              if (cond) {
                char_cond = new CharacterCondition();
                char_cond.source_id = condition_id;
                // char_cond.copy_source({ char: new_obj, feature_source: cond });
                new_obj.conditions.push(char_cond);
              }
            }
            if (char_cond) {
              char_cond.level = level;
            }
            // new_obj.condition_hash[condition_id] = { level, source_true_id: "" };
          }
          if (onChange) {
            onChange(new_obj);
          }
          setPing(!ping);
        }}
        onStar={(id: string) => {
        }}
        onImmune={(id: string) => {
          const new_obj = creature_instance;
          if (new_obj.condition_immunities.includes(id)) {
            new_obj.spirit.condition_immunities = new_obj.condition_immunities.filter(o => o !== id);
          } else {
            new_obj.condition_immunities.push(id);
          }
          // const log = new EncounterLog();
          // log.field = "condition_immunities";
          // log.value = new_obj.condition_immunities;
          if (onChange) {
            onChange(new_obj);
          }
          setPing(!ping);
        }}
      />
    );
  }

  const renderCR = () => {
    if (ci instanceof TrueCreatureInstance) {
      return (
        <p><strong>CR</strong> {ci.challenge_rating} (XP {common_util.commatizeNumber(XP_FOR_CR[ci.challenge_rating])}; PB +{ci.proficiency_modifier})</p>
      );
    }
    return null;
  }

  const renderResources = () => {
    const return_us: any[] = [];
    if (!reload_resources) {
      if (creature_instance.use_resources.length > 0) {
        return_us.push(
          <Grid
            key="resources"
            className='header'
            style={{ borderTop: "1px solid" }}
            size={{
              xs: 12
            }}>Resources</Grid>
        );
      }
      for (const r of creature_instance.use_resources) {
        // Resources are named by type_id now, slots show up in use_resources too but aren't CharacterResources
        const resource = creature_instance.resource_holder.resources.find(o => !o.is_slot && o.type_id === r.resource_id);
        if (resource) {
          return_us.push(
            <Grid
              key={r.resource_id}
              size={{
                xs: 12
              }}>
              <CharacterResourceBoxesNew 
                label={resource.name !== "" ? resource.name : resource.type_id}
                // type={resource.use_name}
                show_type={false}
                used={r.used} 
                total={r.total} 
                true_total={r.total} 
                created={0} 
                shared={false}
                onChange={(used: number) => {
                  const r2 = creature_instance.use_resources.find(o => o.resource_id === r.resource_id);
                  if (r2) {
                    r2.used = used;
                    // const log = new EncounterLog();
                    // log.field = `resources`;
                    // log.value = creature_instance.use_resources;
                    if (onChange) {
                      onChange(creature_instance);
                    }
                    setPing(!ping);
                  }
                }}
              />
            </Grid>
          );
        }
      }
    }
    return return_us;
  }

  const getDisplaySpells = (spell_list: iCreatureSpell[]) => {
    const display_spells: any[] = [];
    for (let i = 0; i < spell_list.length; ++i) {
      const c_spell = spell_list[i];
      display_spells.push(
        <span key={c_spell.spell_id}>
          { i > 0 && ", " }
          <DisplayObjects type="spell" ids={[c_spell.spell_id]} 
            onClick={(obj: ModelBase) => {
              const new_spell = obj as Spell;
              if (selected_spell && new_spell._id === selected_spell._id) {
                setSelectedSpell(null);
              } else {
                setSelectedSpell(new_spell);
              }
            }} 
          />
          { c_spell.extra_text ? c_spell.extra_text : (c_spell.cast_level ? ` (Cast at Level ${c_spell.cast_level})` : "") }
        </span>
      );
    }
    return display_spells;
  }

  const renderSpells = () => {
    // debugger;
    const levels = Object.keys(creature_instance.use_spells).map(o => +o).sort((a,b) => { return +a < +b ? -1 : 1; });
    let return_us: any[] = [];
    if (levels.length > 0) {
      return_us.push(
        <Grid key="spells_header" size={12}
          className="padded">
          <b className="clickable" onClick={() => { 
            setExpanded(draft => {
              if (draft.spells) {
                delete draft.spells;
              } else {
                draft.spells = true;
              }
            });
          }}>Spells</b>
        </Grid>
      );
    }
    if (expanded.spells) {
      if (creature_instance.spellcasting_description !== "") {
        return_us.push(
          <Grid key="spellcasting_desc" size={12}
            className="padded">
            <DisplayStringWithReplacements
              display_string={creature_instance.spellcasting_description} cfs={creature_instance}
            />
          </Grid>
        );
      }
      const slot: any[] = [];
      const at_will: any[] = [];
      const x_day: any[] = [];
      for (const level of levels) {
        const spells_of_level = creature_instance.use_spells.filter(o => o.spell_level === level);
        if (level === 0 && spells_of_level.length > 0) {
          at_will.push(
            <Grid key={level} size={12}
              className="padded">
              Cantrips:&nbsp;
              <DisplayObjects type="spell" ids={spells_of_level.map(o => o.spell_id)} 
                onClick={(obj: ModelBase) => {
                  const new_spell = obj as Spell;
                  if (selected_spell && new_spell._id === selected_spell._id) {
                    setSelectedSpell(null);
                  } else {
                    setSelectedSpell(new_spell);
                  }
                }} 
              />
            </Grid>
          );
        } else {
          // debugger;
          let level_name = "";
          switch(level) {
            case 1: level_name = "1st"; break;
            case 2: level_name = "2nd"; break;
            case 3: level_name = "3rd"; break;
            default: level_name = `${level}th`; break;
          }
          const at_will_spells = spells_of_level.filter(o => o.cast_type === "At Will");
          if (at_will_spells.length > 0) {
            const display_spells = getDisplaySpells(at_will_spells);
            at_will.push(
              <Grid key={level} size={12} className="padded">
                At Will:&nbsp;{ display_spells }
              </Grid>
            );
          }
          const slot_spells = spells_of_level.filter(o => o.cast_type === "Slot");
          const slot_count = creature_instance.use_slots2.find(o => o.resource_id === `Slot: ${level}`);
          if (slot_spells.length > 0 && slot_count && slot_count.total > 0) {
            const display_spells = getDisplaySpells(slot_spells);
            slot.push(
              <Grid key={level} size={12} className="padded">
                { level_name } level{ slot_count ? ` (${slot_count.total} Slots)` : "" }:&nbsp;{ display_spells }
              </Grid>
            );
            if (creature_instance instanceof TrueCreatureInstance && !reload_resources) {
              // slot.push(
              //   <Grid key={`${level}_slots`} size={12}
              //     className="padded">
              //     <CharacterResourceBoxesNew 
              //       used={creature_instance.used_slots[level] || 0}
              //       total={slot_count.total}
              //       true_total={slot_count.total}
              //       created={0}
              //       show_type={false}
              //       shared={false}
              //       onChange={(used: number) => {
              //         const new_obj = creature_instance as TrueCreatureInstance;
              //         new_obj.used_slots[level] = used;
              //         // const log = new EncounterLog();
              //         // log.field = "used_slots";
              //         // log.value = new_obj.used_slots;
                      
              //         if (onChange) {
              //           onChange(new_obj);
              //         }
              //         setPing(!ping);
              //       }}
              //     />
              //   </Grid>
              // );
            }
          }
          const x_day_spells = spells_of_level.filter(o => o.cast_type === "X/Day");
          if (x_day_spells.length > 0) {
            // I should probably change this to happen on copy/connectCreature
            const spellcasting_entry = creature_instance.creature ? creature_instance.creature.spellcasting_table.getEntry(creature_instance.challenge_rating) : undefined;
            if (spellcasting_entry) {
              const spell_ids_by_x: { [x: number]: string[] } = {};
              for (const x_day_spell of x_day_spells) {
                const x = spellcasting_entry.spells.filter(o => o.spell_id === x_day_spell.spell_id)[0].x;
                if (x) {
                  if (!spell_ids_by_x[x]) {
                    spell_ids_by_x[x] = [];
                  }
                  spell_ids_by_x[x].push(x_day_spell.spell_id);
                }
              }
              if (!reload_resources) {
                for (const ex of Object.keys(spell_ids_by_x).map(o => +o)) {
                  const spell_ids = spell_ids_by_x[ex];
                  const display_spells = getDisplaySpells(x_day_spells.filter(o => o.x === ex));
                  x_day.push(
                    <Grid key={`${level}_${ex}`} size={12} className="padded">
                      { level_name } level ({ ex }/Day{ spell_ids.length > 1 ? " each" : "" }):&nbsp;{ display_spells }
                    </Grid>
                  );
                }
              }
            }
          }
        }
      }
      return_us = [...return_us,...at_will,...x_day,...slot];
      
      if (selected_spell) {
        const c_spell = creature_instance.use_spells.find(o => o.spell_id === selected_spell._id);
        return_us.push(
          <Grid
            key="selected_spell"
            container
            spacing={0}
            direction="row"
            className="padded"
            size={12}>
            <Grid size={12} style={{ fontWeight: "bold" }}>
              { selected_spell.use_name }
            </Grid>
            <Grid size={12}>
              <DisplayStringWithReplacements
                display_string={selected_spell.description} 
                cfs={creature_instance}
              />
            </Grid>
            { creature_instance instanceof TrueCreatureInstance && selected_spell.effects.map((effect, key) => {
              return (
                <Grid key={effect.true_id} size={12}>
                  <CreatureEffect 
                    creature_instance={creature_instance} 
                    effect={effect} 
                    spell={selected_spell}
                    c_spell={c_spell}
                    processing={false} 
                    disabled={disabled} 
                    shared={false} 
                    level={selected_spell.level} 
                    wide={false} 
                    onSummon={(selected_option: SummonOption2) => {
                      if (onSummon) {
                        onSummon(selected_spell, selected_spell.level, selected_option);
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        );
      }
    }
    return return_us;
  }

  const onDrop = (acceptedFiles: File[]) => {
    console.log(acceptedFiles);
    if (acceptedFiles.length > 0) {
      const image = acceptedFiles[0];
      const data = new FormData();
      data.append("file", image);
      data.append("upload_preset", "o3zenvnq");
      data.append("cloud_name", "dojs5friw");
      fetch("https://api.cloudinary.com/v1_1/dojs5friw/image/upload", {
        method: "post",
        body: data
      })
        .then(resp => resp.json())
        .then(data => {
          const obj = creature_instance;
          // if (image_type === "Character") {
          //   obj.image_url = data.url;
          // } else {
          //   obj.bg_image_url = data.url;
          // }
          obj.image_url = data.url;
          if (char) {
            char.changed();
          } else if (onChange) {
            onChange(obj);
          }
        })
        .catch(err => console.log(err));
    }
  }
  const renderImageControl = () => {
    return (
      <FileDropzone 
        label={`Drag 'n' drop new Image here` }
        accept={{'image/jpeg': [], 'image/png': []}} 
        onDrop={onDrop} 
      />
    );
  } 

  console.log(ci);
  console.log(reloading);
  console.log(fetched);
  console.log(loading);
  if (reloading || !fetched || loading) {
    return (<LoadingBox />);
  } else {
    const ci_image_style: React.CSSProperties = { };
    if (ci.image_url !== "" && !notes_open && !image_open)  { 
      ci_image_style.backgroundImage = `url(${ci.image_url})`;
      ci_image_style.backgroundSize = "contain";
    }
    return (
      <div className="stat-block">
        { changing_name ? 
          <StringBox
            name="Name"
            value={ci.name}
            onBlur={(changed: string) => {
              const new_obj = creature_instance;
              new_obj.name = changed;
              if (onChange) {
                onChange(new_obj);
              }
              setChangingName(false);
            }}
          />
        : 
          <h4 className="compendium-hr clickable" onClick={() => {
            setChangingName(true);
          }}>{ci.name}</h4>
        }
        <Grid container spacing={0} direction="row">
          <Grid size={6}>
            <p>
              { ci.use_size === undefined ? "Undefined" : SIZE_MAP[ci.use_size] }&nbsp;
              {ci.creature_type_string}, {ci.alignment}&nbsp;
            </p>
            { !notes_open && !image_open && 
              <>
                <p>
                  <span>
                    <strong>AC</strong>&nbsp;
                    {ci.use_ac}{ci.creature && ci.creature.ac_flavor.length > 0 ? ` (${ci.creature.ac_flavor})` : ""}
                  </span>
                  <span style={{ float: "right" }}>
                    <strong>Initiative</strong>&nbsp; 
                    {common_util.addPlusMaybe(ci.initiative_modifier)} ({ci.passive_initiative})
                  </span>
                </p>
                { renderHP() }
                <p><strong>Speed</strong> {ci.speed_string}</p>
              </>
            }
          </Grid>
          <Grid size={6} style={ci_image_style}>
            <span style={{ float: "right" }}>
              { !view_only && !disabled &&
                <CharacterSettings
                  char={ci}
                  onOpen={() => { settings_open.current = true; }}
                  onClose={settingsClosed}
                />
              }
              <NotesButton
                onClick={() => { setNotesOpen(!notes_open); setImageOpen(false); }}
              />
              <BasicImageButton
                onClick={() => { setImageOpen(!image_open); setNotesOpen(false); }}
              />
            </span>
          </Grid>
        </Grid>
        { notes_open ?
          <>
            {/* <StringBox
              name="Notes"
              value={ci.notes}
              disabled={disabled}
              multiline
              onBlur={(changed: string) => {
                const new_obj = creature_instance;
                new_obj.notes = changed;
                if (onChange) {
                  onChange(new_obj);
                }
              }}
            /> */}
          </>
        : image_open ? renderImageControl()
        :
          <>
            { renderStats() }
            { renderSkills() }
            { renderVulnerabilities() }
            { renderResistances() }
            { renderImmunities() }
            { renderSenses() }
            { renderLanguages() }
            { renderCreatureInstanceConditions() }
            { renderCR() }
            { renderResources() }
            <CreatureInstanceActionsInput 
              char={char}
              creature_instance={creature_instance} 
              true_id={true_id}
              trim={trim}
              view_only={view_only}
              onChange={onChange}
              onSummon={onSummon}
            />
          </>
        }
        { renderExtras() }
      </div>
    );
  }
}
